import { useContext, useState, useEffect } from "react";
import { PhoneInput } from "react-international-phone";
import AuthContext from "../context/auth/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import useTranslator from "../hooks/useTranslator";
import { Button, Control, Field, Icon, Modal, TextField } from "./design";
import "./EditProfileModal.css";

const EditProfileModal = ({ show, onClose }) => {
  const { _t } = useTranslator();

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { user, updateProfile } = authContext;
  const { workspaceId } = useParams();

  const [phoneNumber, setPhoneNumber] = useState();
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [LandlordProfile, setLandlordProfile] = useState({
    fname_companyName: "",
    lname: "",
    cif_dni_nie: "",
    phone: "",
    linkedin_profile: "",
    email: "",
    type: "landlord",
    is_edit: true,
  });

  // ✅ VALIDATE FIELD
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "fname_companyName":
        if (!value.trim()) error = _t("firstNameIsRequired");
        else if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(value))
          error = _t("firstNameInvalid");
        break;

      case "lname":
        if (!value.trim()) error = _t("lastNameIsRequired");
        else if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(value))
          error = _t("lnameNameInvalid");
        break;

      case "email":
        if (!value.trim()) error = _t("emailIsRequired");
        else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value.trim()))
          error = _t("invalidEmailFormat");
        break;

      case "phone":
        if (!value || value.length <= 5)
          error = _t("phoneNumberMustBeAtLeastSixDigits");
        break;

      default:
        break;
    }

    return error;
  };

  // ✅ HANDLE INPUT CHANGE + VALIDATE
  const onChangeLandlord = (e) => {
    const { name, value } = e.target;

    setLandlordProfile((prev) => ({
      ...prev,
      [name]: value,
    }));

    // validate + update error
    const error = validateField(name, value);

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // ✅ PHONE CHANGE + VALIDATE
  const onPhoneChange = (value) => {
    const phone = value?.replace("+", "") || "";

    setPhoneNumber(phone);

    setLandlordProfile((prev) => ({
      ...prev,
      phone: phone,
    }));

    // const error = validateField("phone", phone);

    // setErrors((prev) => ({
    //   ...prev,
    //   phone: error,
    // }));
  };

  // ✅ FORM VALIDATION
  const isFormValid = () => {
    const newErrors = {};

    const fields = [
      { key: "fname_companyName", value: LandlordProfile.fname_companyName },
      { key: "lname", value: LandlordProfile.lname },
      { key: "phone", value: LandlordProfile.phone },
    ];

    fields.forEach(({ key, value }) => {
      const err = validateField(key, value);
      if (err) newErrors[key] = err;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ SUBMIT
  const onSubmitLandlord = async (e) => {
    e.preventDefault();

    if (saving || !isFormValid()) return;

    const profileWithoutEmail = { ...LandlordProfile };
    delete profileWithoutEmail.email;
    setSaving(true);
    const ok = await updateProfile(
      user?._id,
      { ...profileWithoutEmail, role: "landlord", is_edit: true },
      { silent: true }
    );
    setSaving(false);
    if (ok) onClose();
  };

  const closeButton = () => {
    navigate(`/${workspaceId}/landlord-profile`);
  };

  useEffect(() => {
    if (LandlordProfile?.email) {
      return;
    }
    if (user) setLandlordProfile(user);
  }, [user]);

  useEffect(() => {
    setPhoneNumber(LandlordProfile?.phone || "");
  }, [LandlordProfile]);

  // letters, spaces, apostrophes and hyphens only (same rule as before)
  const onlyNameCharacters = (e) => {
    if (e.data && !/^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data)) e.preventDefault();
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      size="md"
      align="start"
      title={_t("editProfile")}
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={onClose}
            disabled={saving}
          >
            {_t("cancel")}
          </Button>
          <Button
            type="submit"
            form="edit-profile-form"
            size="lg"
            loading={saving}
          >
            {_t("saveChanges")}
          </Button>
        </>
      }
    >
      <form
        id="edit-profile-form"
        className="edit-profile-form"
        onSubmit={onSubmitLandlord}
        noValidate
      >
        <div className="edit-profile-form__row">
          <TextField
            id="edit-profile-fname"
            name="fname_companyName"
            label={_t("firstName")}
            placeholder={_t("enterFirstName")}
            value={LandlordProfile?.fname_companyName || ""}
            onChange={onChangeLandlord}
            onBeforeInput={onlyNameCharacters}
            error={errors.fname_companyName}
          />
          <TextField
            id="edit-profile-lname"
            name="lname"
            label={_t("lastName")}
            placeholder={_t("enterLastName")}
            value={LandlordProfile?.lname || ""}
            onChange={onChangeLandlord}
            onBeforeInput={onlyNameCharacters}
            error={errors.lname}
          />
        </div>

        <Field label={_t("phoneNumber")} error={errors.phone}>
          <Control>
            <PhoneInput
              international
              defaultCountry={"es"}
              value={phoneNumber}
              onChange={onPhoneChange}
            />
          </Control>
        </Field>

        <TextField
          id="edit-profile-linkedin"
          name="linkedin_profile"
          label={_t("linkedInProfile")}
          placeholder={_t("enterLinkedInProfile")}
          value={LandlordProfile?.linkedin_profile || ""}
          onChange={onChangeLandlord}
        />

        <TextField
          id="edit-profile-email"
          name="email"
          label={_t("email")}
          icon={<Icon name="lock" />}
          value={LandlordProfile?.email || ""}
          disabled
        />
      </form>
    </Modal>
  );
};

export default EditProfileModal;
